import React, { useEffect } from 'react';
import { Outlet, useParams, useNavigate, useSearchParams } from 'react-router';
import {
  Button,
  Card,
  CardFooter,
  CardSection,
  Grid,
  GridColumn,
  GridRow,
  Gutter,
  Layout,
  LayoutItem,
  Paragraph,
  SmallHeader,
  useApi,
} from '@axiom/ui';

import { EngagementsAccordion } from '../EngagementsAccordion/EngagementsAccordion';
import { CandidateEngagementsApi } from '../../api/candidate-engagements-api';
import { EngagementsUtil } from '../../utils/engagements-util';
import { useCandidateId } from '../../hooks/useCandidateId';
import MapGraphic from '../../public/svg/map.svg';

export const EngagementsView = () => {
  const candidateId = useCandidateId();
  const { opportunityId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [{ data: engagementsData }, { data: engagementsCount }] = useApi(
    CandidateEngagementsApi.readEngagementsCurrent(candidateId),
    CandidateEngagementsApi.readEngagementsCount(candidateId)
  );

  const { engagements, upcomingEngagements, opportunities, submissions } =
    EngagementsUtil.partitionCurrentEngagements({
      engagements: engagementsData,
    });

  const counts = EngagementsUtil.getCurrentEngagementsCounts({
    partitionedEngagements: {
      engagements,
      upcomingEngagements,
      opportunities,
      submissions,
    },
  });

  useEffect(() => {
    if (!opportunityId) {
      if (
        searchParams.get('modal') === 'warming' &&
        engagementsData.some(
          engagement => engagement.opportunity.id === searchParams.get('id')
        )
      ) {
        void navigate(`./${searchParams.get('id')}?modal=warming`, {
          replace: true,
        });
      } else if (engagements[0]) {
        void navigate(`./${engagements[0].opportunity.id}`, {
          replace: true,
        });
      } else if (upcomingEngagements[0]) {
        void navigate(`./${upcomingEngagements[0].opportunity.id}`, {
          replace: true,
        });
      } else if (opportunities[0]) {
        void navigate(`./${opportunities[0].opportunity.id}`, {
          replace: true,
        });
      } else if (submissions[0]) {
        void navigate(`./${submissions[0].opportunity.id}`, {
          replace: true,
        });
      }
    } else if (
      !engagementsData.some(
        engagement => engagement.opportunity.id === opportunityId
      )
    ) {
      const historyPageHasContent =
        engagementsCount.pastInterestHistoryTotal +
          engagementsCount.pastSubmissionHistoryTotal +
          engagementsCount.pastEngagementHistoryTotal >
        0;
      void navigate(`.${historyPageHasContent ? '/history' : ''}`, {
        replace: true,
      });
    }
  }, [engagementsData, engagementsCount, opportunityId]);

  return engagementsData.length === 0 ? (
    <Grid name="ENGAGEMENTS_VIEW_EMPTY_LAYOUT" centered>
      <GridRow>
        <GridColumn largeScreenWidth={8} smallScreenWidth={12}>
          <Card>
            <CardSection divider>
              <Layout
                stackableOn="mobile"
                horizontalGutter="24px"
                verticalGutter="24px"
              >
                <LayoutItem>
                  <MapGraphic width="75px" height="80px" />
                </LayoutItem>
                <LayoutItem fluid>
                  <SmallHeader>
                    Track your Axiom engagements from start to finish
                  </SmallHeader>
                  <Gutter bottom="16px" />
                  <Paragraph>
                    Opportunities you're interested in, client submissions, and
                    active engagements will appear here where you can review
                    details and monitor progress.
                    <br />
                    <br />
                    Nothing here to track right now? This is a great time to
                    review your profile and make sure it's up to date.
                  </Paragraph>
                </LayoutItem>
              </Layout>
            </CardSection>
            <CardFooter>
              <Button to="../profile">Go to profile</Button>
            </CardFooter>
          </Card>
        </GridColumn>
      </GridRow>
    </Grid>
  ) : (
    <div data-test="ENGAGEMENTS-V2-VIEW">
      <Grid>
        <GridRow>
          <GridColumn largeScreenWidth={4} smallScreenWidth={12}>
            <Layout verticalGutter="16px" direction="vertical">
              {engagements.length > 0 && (
                <LayoutItem>
                  <EngagementsAccordion
                    countBadge={counts.engagements}
                    name="ACTIVE_ENGAGEMENTS"
                    title="Active Engagements"
                    engagements={engagements}
                  />
                </LayoutItem>
              )}
              {upcomingEngagements.length > 0 && (
                <LayoutItem>
                  <EngagementsAccordion
                    countBadge={counts.upcomingEngagements}
                    name="UP_COMING_ENGAGEMENTS"
                    title="Upcoming Engagements"
                    engagements={upcomingEngagements}
                  />
                </LayoutItem>
              )}
              {opportunities.length > 0 && (
                <LayoutItem>
                  <EngagementsAccordion
                    countBadge={counts.opportunities}
                    name="ACTIVE_OPPORTUNITIES"
                    title="Prospective Opportunities"
                    engagements={opportunities}
                  />
                </LayoutItem>
              )}
              {submissions.length > 0 && (
                <LayoutItem>
                  <EngagementsAccordion
                    countBadge={counts.submissions}
                    name="ACTIVE_SUBMISSIONS"
                    title="Active Submissions"
                    engagements={submissions}
                  />
                </LayoutItem>
              )}
            </Layout>
            <Gutter bottom="24px" />
          </GridColumn>
          <GridColumn largeScreenWidth={8} smallScreenWidth={12}>
            <Outlet />
          </GridColumn>
        </GridRow>
      </Grid>
    </div>
  );
};
